import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, UserRound, Quote, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useFetch, fileUrl, usePageTitle } from "@/lib/api";
import { BRAND } from "@/lib/content";
import { Loading, PalmSilhouette, SectionHeading } from "@/components/site/Bits";

const Splatter = ({ className = "" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="currentColor" aria-hidden="true">
    <path d="M104 38c18 2 30 20 44 30s34 10 36 30-18 26-22 44-2 38-22 40-28-16-46-20-40 4-46-14 12-30 10-48-14-34 2-46 26-18 44-16z" />
    <circle cx="186" cy="40" r="7" /><circle cx="20" cy="150" r="9" /><circle cx="170" cy="182" r="5" /><circle cx="36" cy="30" r="4" /><circle cx="196" cy="120" r="3" />
  </svg>
);

const Photo = ({ m, className = "" }) => (
  <div className={`relative overflow-hidden bg-gradient-to-br from-navy to-pitch ${className}`}>
    {m.photo_url ? <img src={fileUrl(m.photo_url)} alt={m.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="absolute inset-0 stripes flex items-center justify-center"><UserRound className="h-16 w-16 text-line" /></div>}
    <div className="absolute inset-0 halftone mix-blend-overlay pointer-events-none" />
  </div>
);

const Card = ({ m, i, onOpen }) => (
  <motion.button
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
    onClick={() => onOpen(m)}
    className="group relative text-left bg-ink border border-line hover:border-gold transition-[border-color,transform] duration-300 hover:-translate-y-1 overflow-hidden"
    data-testid={`board-card-${m.id}`}
  >
    <div className="relative aspect-[4/5]">
      <Photo m={m} className="absolute inset-0 transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-pitch via-pitch/20 to-transparent" />
      <span className="absolute right-2 top-1 font-display text-6xl sm:text-7xl font-black italic leading-none text-stroke">{String(i + 1).padStart(2, "0")}</span>
      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-5">
        <p className="font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.2em] text-gold leading-tight">{m.title}</p>
        <h3 className="mt-1 font-display text-2xl sm:text-4xl font-black uppercase italic leading-none" data-testid={`board-card-name-${m.id}`}>{m.name}</h3>
      </div>
    </div>
    <div className="p-3 sm:p-5 border-t border-line">
      <ul className="hidden sm:block space-y-1 text-sm text-slate-300 mb-3">
        {(m.responsibilities || []).slice(0, 2).map((r) => <li key={r} className="flex gap-2"><span className="text-gold">▸</span>{r}</li>)}
      </ul>
      <span className="inline-flex items-center gap-1.5 font-display text-sm sm:text-base font-bold uppercase text-gold group-hover:text-white transition-colors">View Profile <ArrowRight className="h-4 w-4" /></span>
    </div>
  </motion.button>
);

const Profile = ({ m, onClose }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl bg-ink border-line p-0 overflow-hidden max-h-[92svh] overflow-y-auto" data-testid="board-profile-dialog">
      <div className="grid sm:grid-cols-[0.9fr_1.1fr]">
        <Photo m={m} className="aspect-[4/5] sm:aspect-auto sm:min-h-full" />
        <div className="p-6 sm:p-8">
          <p className="eyebrow">{m.title}</p>
          <DialogTitle className="mt-2 h-display text-5xl">{m.name}</DialogTitle>
          <DialogDescription className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed whitespace-pre-line" data-testid="board-profile-bio">
            {m.bio || "Biography coming soon."}
          </DialogDescription>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Primary Responsibilities</p>
          <ul className="mt-3 space-y-2" data-testid="board-profile-responsibilities">
            {(m.responsibilities || []).map((r) => <li key={r} className="flex gap-2 text-sm"><span className="text-gold">▸</span>{r}</li>)}
          </ul>
          {m.message && (
            <div className="mt-6 border-l-4 border-gold bg-pitch/60 p-4" data-testid="board-profile-message">
              <Quote className="h-5 w-5 text-gold" />
              <p className="mt-2 text-sm italic text-slate-200 whitespace-pre-line">{m.message}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">— A message to parents</p>
            </div>
          )}
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const WhoToContact = ({ members }) => (
  <section className="relative bg-navy py-16 sm:py-24 overflow-hidden" data-testid="board-contact-section">
    <Splatter className="absolute -left-10 top-6 h-56 text-gold/10" />
    <div className="relative wrap grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
      <div>
        <SectionHeading eyebrow="Get the Right Answer" title={<>Not Sure Who <span className="text-gold">to Contact?</span></>} sub="Here's who handles each area of Tri County Allstars. Send your question through our contact page and it will reach the right person." />
        <Link to="/contact" className="btn-gold mt-8" data-testid="board-contact-button"><MessageSquare className="h-5 w-5" /> Contact Us</Link>
      </div>
      <div className="space-y-px bg-line/60">
        {members.filter((m) => m.contact_for).map((m) => (
          <div key={m.id} className="bg-navy flex items-center justify-between gap-4 py-4" data-testid={`board-contact-row-${m.id}`}>
            <p className="text-sm sm:text-base text-slate-200">{m.contact_for}</p>
            <p className="font-display text-lg sm:text-xl font-black uppercase italic text-gold text-right shrink-0">{m.name}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Board() {
  usePageTitle("Meet the Board");
  const { data, loading } = useFetch("/content/board");
  const [open, setOpen] = useState(null);
  const members = data || [];
  return (
    <div data-testid="board-page">
      <section className="relative overflow-hidden grain bg-navy pt-36 pb-16 sm:pt-44 sm:pb-24" data-testid="board-hero">
        <div className="absolute inset-0 halftone opacity-60" />
        <Splatter className="absolute right-[-60px] top-16 h-80 sm:h-[28rem] text-gold/15" />
        <Splatter className="absolute left-[30%] bottom-[-80px] h-48 text-white/5 rotate-45" />
        <PalmSilhouette className="absolute -left-10 bottom-0 h-[70%] text-pitch/50" />
        <div className="absolute bottom-0 inset-x-0 h-2 bg-gold" />
        <div className="relative wrap grid gap-10 lg:grid-cols-[1.4fr_0.6fr] items-center">
          <div>
            <p className="eyebrow mb-5">The People Behind Tri County Allstars</p>
            <motion.h1 initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="h-display text-6xl sm:text-7xl lg:text-8xl">
              Meet the <span className="text-gold">Board</span>
            </motion.h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-200 italic border-l-4 border-gold pl-4">
              “Every opportunity we create for our athletes starts with a dedicated team working behind the scenes. Meet the individuals committed to building something special for the Treasure Coast.”
            </p>
          </div>
          <img src={BRAND.logo} alt="Tri County Allstars logo" className="hidden lg:block w-full max-w-[300px] justify-self-end drop-shadow-[0_20px_50px_rgba(0,0,0,0.6)]" />
        </div>
      </section>
      <section className="bg-pitch py-14 sm:py-20">
        <div className="wrap">
          {loading ? <Loading /> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="board-grid">
              {members.map((m, i) => <Card key={m.id} m={m} i={i} onOpen={setOpen} />)}
            </div>
          )}
        </div>
      </section>
      <WhoToContact members={members} />
      {open && <Profile m={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
